import { t } from "i18n";
import { expireSession } from "utils/authSession";

const CACHE_TTL = 2 * 60 * 1000; // 2 minutes

// ============================================
// Auth Endpoints
// ============================================

/**
 * Register a new user
 * POST /v2/auth/register/
 */
const register = async ({ body }) => {
  return await v2Client({
    url: "/v2/auth/register/",
    method: "POST",
    body,
    isAuthenticated: false,
  });
};

/**
 * Login user
 * POST /v2/auth/login/
 */
const login = async ({ body }) => {
  return await v2Client({
    url: "/v2/auth/login/",
    method: "POST",
    body,
    isAuthenticated: false,
  });
};

/**
 * Logout user
 * POST /v2/auth/logout/
 */
const logout = async () => {
  return await v2Client({
    url: "/v2/auth/logout/",
    method: "POST",
    isAuthenticated: true,
  });
};

/**
 * Verify email with token
 * GET /v2/auth/verify/?token=X
 */
const verifyEmail = async ({ token }) => {
  return await v2Client({
    url: "/v2/auth/verify/",
    method: "GET",
    queryParams: { token },
    isAuthenticated: false,
  });
};

/**
 * Resend verification email
 * POST /v2/auth/verify/send-email/
 */
const resendVerificationEmail = async () => {
  return await v2Client({
    url: "/v2/auth/verify/send-email/",
    method: "POST",
    isAuthenticated: true,
  });
};

/**
 * Request password reset
 * POST /v2/auth/password/reset/
 */
const requestPasswordReset = async ({ email }) => {
  return await v2Client({
    url: "/v2/auth/password/reset/",
    method: "POST",
    body: { email },
    isAuthenticated: false,
  });
};

/**
 * Confirm password reset with token
 * POST /v2/auth/password/reset/confirm/
 */
const confirmPasswordReset = async ({ token, password }) => {
  return await v2Client({
    url: "/v2/auth/password/reset/confirm/",
    method: "POST",
    body: { token, password },
    isAuthenticated: false,
  });
};

// ============================================
// User Endpoints
// ============================================

/**
 * Get current authenticated user
 * GET /v2/me/
 */
const getCurrentUser = async () => {
  return await v2Client({
    url: "/v2/me/",
    method: "GET",
    isAuthenticated: true,
  });
};

/**
 * Update current user profile
 * PATCH /v2/me/
 * Note: Email changes require a confirmation_token in the body
 */
const updateCurrentUser = async ({ body }) => {
  return await v2Client({
    url: "/v2/me/",
    method: "PATCH",
    body,
    isAuthenticated: true,
  });
};

/**
 * Confirm password to get a token for sensitive operations
 * POST /v2/me/confirm-password/
 */
const confirmPassword = async ({ password }) => {
  return await v2Client({
    url: "/v2/me/confirm-password/",
    method: "POST",
    body: { password },
    isAuthenticated: true,
    // 401 here means the password was wrong, not that the session ended.
    expireSessionOn401: false,
  });
};

/**
 * Change password
 * POST /v2/me/change-password/
 * Requires a confirmation_token from confirmPassword
 */
const changePassword = async ({ confirmationToken, newPassword }) => {
  return await v2Client({
    url: "/v2/me/change-password/",
    method: "POST",
    body: {
      confirmation_token: confirmationToken,
      new_password: newPassword,
    },
    isAuthenticated: true,
  });
};

/**
 * Delete current user account
 * DELETE /v2/me/
 * Requires a confirmation_token from confirmPassword
 */
const deleteAccount = async ({ confirmationToken }) => {
  return await v2Client({
    url: "/v2/me/",
    method: "DELETE",
    body: { confirmation_token: confirmationToken },
    isAuthenticated: true,
  });
};

/**
 * Export all personal data for the current user (GDPR)
 * GET /v2/me/data/
 */
const exportMyData = async () => {
  return await v2Client({
    url: "/v2/me/data/",
    method: "GET",
    isAuthenticated: true,
  });
};

/**
 * Delete all personal data for the current user (GDPR)
 * DELETE /v2/me/data/
 * Requires a confirmation_token from confirmPassword
 */
const deleteMyData = async ({ confirmationToken }) => {
  return await v2Client({
    url: "/v2/me/data/",
    method: "DELETE",
    body: { confirmation_token: confirmationToken },
    isAuthenticated: true,
  });
};

/**
 * Selectively delete personal data without deleting account (GDPR)
 * POST /v2/me/data/selective-delete/
 * Requires a confirmation_token from confirmPassword
 * mode: "all" | "date_range"
 * date_from / date_to: required when mode is "date_range" (ISO date strings)
 */
const selectiveDeleteMyData = async ({
  confirmationToken,
  mode,
  dateFrom,
  dateTo,
}) => {
  const body = {
    confirmation_token: confirmationToken,
    mode,
  };
  if (mode === "date_range") {
    body.date_from = dateFrom;
    body.date_to = dateTo;
  }
  return await v2Client({
    url: "/v2/me/data/selective-delete/",
    method: "POST",
    body,
    isAuthenticated: true,
  });
};

// V2 API uses /v2/ prefix instead of /api/
// We pass the full path and let v2Client handle it, but we need to
// strip /api from the base URL for V2 calls
const V2_BASE =
  process.env.REACT_APP_SIT_API_URL?.replace(/\/api\/?$/, "") || "";

// In-memory read cache + in-flight dedup for GETs. A GET that passes `cacheTTL`
// is served from cache while fresh, and concurrent identical GETs share one
// request. Any successful write (non-GET) clears the whole cache, since a
// mutation may invalidate anything. Pass `forceRefresh: true` to bypass a fresh
// entry. The cache is module-scoped, so it also survives route changes (back
// navigation is instant) but is bounded by the TTL each caller supplies.
const v2ReadCache = new Map(); // key -> { expires, value }
const v2InFlight = new Map(); // key -> Promise<{ status, data }>

/**
 * V2-specific API client wrapper that uses the correct base URL
 */
const v2Client = async (options) => {
  // Construct the full URL manually for V2
  const { url, ...rest } = options;
  const method = (rest.method || "GET").toUpperCase();
  const fullUrl = `${V2_BASE}${url}`;

  const queryString = rest.queryParams
    ? new URLSearchParams(rest.queryParams).toString()
    : "";
  const requestUrl = queryString ? `${fullUrl}?${queryString}` : fullUrl;

  const isGet = method === "GET";
  const ttl = typeof rest.cacheTTL === "number" ? rest.cacheTTL : 0;
  const cacheKey = `${rest.workspaceId || ""}|${requestUrl}`;

  // Serve a fresh cache hit, or join an identical in-flight request.
  if (isGet && ttl > 0 && !rest.forceRefresh) {
    const hit = v2ReadCache.get(cacheKey);
    if (hit && hit.expires > Date.now()) return hit.value;
    const pending = v2InFlight.get(cacheKey);
    if (pending) return pending;
  }

  const run = async () => {
    // We need to bypass v2Client's URL construction, so we'll use fetch directly
    const headers = {
      "Content-Type": "application/json",
    };

    if (rest.isAuthenticated) {
      const authToken =
        localStorage.getItem("authToken") ||
        localStorage.getItem("stakeholderToken");
      if (authToken) {
        headers["Authorization"] = `Token ${authToken}`;
      }
    }

    if (rest.workspaceId) {
      headers["X-SIT-Workspace-ID"] = rest.workspaceId;
    }

    const fetchOptions = { method, headers };

    if (rest.body) {
      fetchOptions.body = JSON.stringify(rest.body);
    }

    const response = await fetch(requestUrl, fetchOptions);

    // Handle 204 No Content (e.g., DELETE responses)
    if (response.status === 204) {
      return { status: response.status, data: null };
    }

    let responseData;
    try {
      responseData = await response.json();
    } catch (err) {
      console.error("Failed to parse JSON:", err);
      throw new Error(t("utils.api.invalidResponse"));
    }

    // A rejected session token, as opposed to a 401 an endpoint returns on
    // purpose (e.g. a wrong password in confirmPassword).
    if (
      response.status === 401 &&
      rest.isAuthenticated &&
      rest.expireSessionOn401 !== false &&
      localStorage.getItem("authToken")
    ) {
      v2ReadCache.clear();
      expireSession();
    }

    if (!response.ok) {
      // Errors are RFC 9457 problem details: detail, then title.
      const errorMessage =
        responseData?.detail ||
        responseData?.title ||
        responseData?.message ||
        t("utils.api.httpError", { status: response.status });
      const e = new Error(errorMessage);
      e.status = response.status;
      e.payload = responseData;
      throw e;
    }

    return { status: response.status, data: responseData };
  };

  // Cacheable GET: register the in-flight promise for dedup, cache on success.
  if (isGet && ttl > 0) {
    const promise = run()
      .then((value) => {
        v2ReadCache.set(cacheKey, { expires: Date.now() + ttl, value });
        return value;
      })
      .finally(() => {
        v2InFlight.delete(cacheKey);
      });
    v2InFlight.set(cacheKey, promise);
    try {
      return await promise;
    } catch (error) {
      console.error("V2 API Error:", error.message, error);
      throw error;
    }
  }

  // Non-cacheable GET, or a mutation (which invalidates the read cache).
  try {
    const value = await run();
    if (!isGet) {
      v2ReadCache.clear();
      v2InFlight.clear();
    }
    return value;
  } catch (error) {
    console.error("V2 API Error:", error.message, error);
    throw error;
  }
};

/**
 * List all workspaces the current user has access to
 * GET /v2/workspaces/
 */
const getWorkspaces = async ({ forceRefresh = false } = {}) => {
  return await v2Client({
    url: "/v2/workspaces/",
    method: "GET",
    isAuthenticated: true,
    cacheTTL: CACHE_TTL,
    forceRefresh,
  });
};

/**
 * Create a new workspace
 * POST /v2/workspaces/
 */
const createWorkspace = async ({ body }) => {
  return await v2Client({
    url: "/v2/workspaces/",
    method: "POST",
    body,
    isAuthenticated: true,
  });
};

/**
 * Get a single workspace
 * GET /v2/workspaces/{workspace_id}/
 */
const getWorkspace = async ({ workspaceId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/`,
    method: "GET",
    isAuthenticated: true,
  });
};

/**
 * Update a workspace (partial update)
 * PATCH /v2/workspaces/{workspace_id}/
 */
const updateWorkspace = async ({ workspaceId, body }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/`,
    method: "PATCH",
    body,
    isAuthenticated: true,
  });
};

/**
 * Delete a workspace
 * Permanently deletes the workspace and all associated data.
 * DELETE /v2/workspaces/{workspace_id}/
 */
const deleteWorkspace = async ({ workspaceId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/`,
    method: "DELETE",
    isAuthenticated: true,
  });
};

/**
 * Leave a workspace (current user removes themselves)
 * POST /v2/workspaces/{workspace_id}/leave/
 * 204 — Left successfully
 * 409 — Only admin; must transfer role first
 * 404 — Not a member
 */
const leaveWorkspace = async ({ workspaceId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/leave/`,
    method: "POST",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Invite a member to a workspace
 * POST /v2/workspaces/{workspace_id}/invite/
 */
const inviteMember = async ({ workspaceId, email, role }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/invite/`,
    method: "POST",
    body: { email, role },
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Get workspace dashboard data (stats, teams, recent activity)
 * GET /v2/workspaces/{workspace_id}/dashboard/
 */
const getDashboard = async ({
  workspaceId,
  queryParams = {},
  forceRefresh = false,
}) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/dashboard/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
    queryParams,
    cacheTTL: CACHE_TTL,
    forceRefresh,
  });
};

/**
 * Get a single team by ID
 * GET /v2/workspaces/{workspace_id}/teams/{team_id}/
 */
const getTeam = async ({ workspaceId, teamId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Create a new team
 * POST /v2/workspaces/{workspace_id}/teams/
 */
const createTeam = async ({ workspaceId, body }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/`,
    method: "POST",
    body,
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Update a team (partial update)
 * PATCH /v2/workspaces/{workspace_id}/teams/{team_id}/
 */
const updateTeam = async ({ workspaceId, teamId, body }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/`,
    method: "PATCH",
    body,
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Delete a team
 * DELETE /v2/workspaces/{workspace_id}/teams/{team_id}/
 */
const deleteTeam = async ({ workspaceId, teamId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/`,
    method: "DELETE",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Create a new player for a team
 * POST /v2/workspaces/{workspace_id}/teams/{team_id}/players/
 */
const createPlayer = async ({ workspaceId, teamId, body }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/players/`,
    method: "POST",
    body,
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Update a player (partial update)
 * PATCH /v2/workspaces/{workspace_id}/teams/{team_id}/players/{player_id}/
 */
const updatePlayer = async ({ workspaceId, teamId, playerId, body }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/players/${playerId}/`,
    method: "PATCH",
    body,
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Delete a player
 * DELETE /v2/workspaces/{workspace_id}/teams/{team_id}/players/{player_id}/
 */
const deletePlayer = async ({ workspaceId, teamId, playerId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/players/${playerId}/`,
    method: "DELETE",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Change a player's team
 * POST /v2/workspaces/{workspace_id}/teams/{team_id}/players/{player_id}/change-team/
 */
const changePlayerTeam = async ({
  workspaceId,
  teamId,
  playerId,
  newTeamId,
}) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/players/${playerId}/change-team/`,
    method: "POST",
    body: { team_id: newTeamId },
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Get team overview (stats, players list)
 * GET /v2/workspaces/{workspace_id}/teams/{team_id}/overview/
 */
const getTeamOverview = async ({
  workspaceId,
  teamId,
  queryParams = {},
  forceRefresh = false,
}) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/overview/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
    queryParams,
    cacheTTL: CACHE_TTL,
    forceRefresh,
  });
};

/**
 * Get team load chart data
 * GET /v2/workspaces/{workspace_id}/teams/{team_id}/load-chart/
 */
const getTeamLoadChart = async ({
  workspaceId,
  teamId,
  queryParams = {},
  forceRefresh = false,
}) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/load-chart/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
    queryParams,
    cacheTTL: CACHE_TTL,
    forceRefresh,
  });
};

/**
 * Get team header-count aggregate (server-side; replaces the per-session
 * detail fan-out). GET /v2/workspaces/{workspace_id}/teams/{team_id}/header-stats/
 * Query params (all optional): start_date, end_date (YYYY-MM-DD),
 * session_type (training|match), threshold_g (default 10).
 * Response: { total_headers, threshold_g, sessions: [{ id, name, date, type, headers }] }
 */
const getTeamHeaderStats = async ({
  workspaceId,
  teamId,
  queryParams = {},
  forceRefresh = false,
}) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/header-stats/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
    queryParams,
    cacheTTL: CACHE_TTL,
    forceRefresh,
  });
};

/**
 * Get player load chart data with peer comparison
 * GET /v2/workspaces/{workspace_id}/teams/{team_id}/players/{player_id}/load-chart/
 */
const getPlayerLoadChart = async ({
  workspaceId,
  teamId,
  playerId,
  queryParams = {},
  forceRefresh = false,
}) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/players/${playerId}/load-chart/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
    queryParams,
    cacheTTL: CACHE_TTL,
    forceRefresh,
  });
};

/**
 * Get workspace activity feed
 * GET /v2/workspaces/{workspace_id}/activity/
 */
const getWorkspaceActivity = async ({
  workspaceId,
  queryParams = {},
  forceRefresh = false,
}) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/activity/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
    queryParams,
    cacheTTL: CACHE_TTL,
    forceRefresh,
  });
};

/**
 * Get team sessions list
 * GET /v2/workspaces/{workspace_id}/teams/{team_id}/sessions/
 */
const getTeamSessions = async ({
  workspaceId,
  teamId,
  queryParams = {},
  forceRefresh = false,
}) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/sessions/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
    queryParams,
    cacheTTL: CACHE_TTL,
    forceRefresh,
  });
};

/**
 * Get a single session by ID
 * GET /v2/workspaces/{workspace_id}/teams/{team_id}/sessions/{session_id}/
 */
const getSession = async ({ workspaceId, teamId, sessionId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/sessions/${sessionId}/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Time-resolved low-g load graph for a session: bin_edges, a team series and
 * per-assignment player series of 30s histogram windows. `bucket` re-buckets
 * server-side (any multiple of 30s, e.g. 300 for a 5-minute graph).
 * GET /v2/workspaces/{workspace_id}/teams/{team_id}/sessions/{session_id}/load-graph/
 */
const getSessionLoadGraph = async ({
  workspaceId,
  teamId,
  sessionId,
  bucket,
}) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/sessions/${sessionId}/load-graph/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
    queryParams: { ...(bucket && { bucket }) },
  });
};

/**
 * Upload impact data for a session assignment
 * POST /v2/workspaces/{workspace_id}/teams/{team_id}/sessions/{session_id}/assignments/{assignment_id}/impact-data/
 */
const uploadImpactData = async ({
  workspaceId,
  teamId,
  sessionId,
  assignmentId,
  body,
}) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/sessions/${sessionId}/assignments/${assignmentId}/impact-data/`,
    method: "POST",
    body,
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Get team attendance data
 * GET /v2/workspaces/{workspace_id}/teams/{team_id}/attendance/
 */
const getTeamAttendance = async ({
  workspaceId,
  teamId,
  queryParams = {},
  forceRefresh = false,
}) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/attendance/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
    queryParams,
    cacheTTL: CACHE_TTL,
    forceRefresh,
  });
};

/**
 * Get team activity feed
 * GET /v2/workspaces/{workspace_id}/teams/{team_id}/activity/
 */
const getTeamActivity = async ({
  workspaceId,
  teamId,
  queryParams = {},
  forceRefresh = false,
}) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/activity/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
    queryParams,
    cacheTTL: CACHE_TTL,
    forceRefresh,
  });
};

/**
 * Admin: List all workspaces (super users only)
 * GET /v2/admin/workspaces/
 */
const getAdminWorkspaces = async ({ page = 1, pageSize = 20, search = "" }) => {
  return await v2Client({
    url: "/v2/admin/workspaces/",
    method: "GET",
    isAuthenticated: true,
    queryParams: {
      page,
      page_size: pageSize,
      ...(search && { search }),
    },
  });
};

/**
 * Admin: a club's parent-consent settings (super users only)
 * GET/PATCH /v2/admin/workspaces/:id/consent-settings/
 * { research_consent_enabled, privacy_notice_url }
 */
const getWorkspaceConsentSettings = async ({ workspaceId }) => {
  return await v2Client({
    url: `/v2/admin/workspaces/${workspaceId}/consent-settings/`,
    method: "GET",
    isAuthenticated: true,
  });
};

const updateWorkspaceConsentSettings = async ({ workspaceId, settings }) => {
  return await v2Client({
    url: `/v2/admin/workspaces/${workspaceId}/consent-settings/`,
    method: "PATCH",
    body: settings,
    isAuthenticated: true,
  });
};

/**
 * Admin: List all users (super users only)
 * GET /v2/admin/users/
 */
const getAdminUsers = async ({ page = 1, pageSize = 20, search = "" }) => {
  return await v2Client({
    url: "/v2/admin/users/",
    method: "GET",
    isAuthenticated: true,
    queryParams: {
      page,
      page_size: pageSize,
      ...(search && { search }),
    },
  });
};

/**
 * Admin: List all teams (super users only)
 * GET /v2/admin/teams/
 */
const getAdminTeams = async ({ page = 1, pageSize = 20, search = "" }) => {
  return await v2Client({
    url: "/v2/admin/teams/",
    method: "GET",
    isAuthenticated: true,
    queryParams: {
      page,
      page_size: pageSize,
      ...(search && { search }),
    },
  });
};

/**
 * Admin: List all players (super users only)
 * GET /v2/admin/players/
 */
const getAdminPlayers = async ({ page = 1, pageSize = 20, search = "" }) => {
  return await v2Client({
    url: "/v2/admin/players/",
    method: "GET",
    isAuthenticated: true,
    queryParams: {
      page,
      page_size: pageSize,
      ...(search && { search }),
    },
  });
};

/**
 * Admin: System-wide counts for the dashboard header + tab badges (one call).
 * GET /v2/admin/overview/ → { workspaces, users, teams, athletes, wearables }
 */
const getAdminOverview = async () => {
  return await v2Client({
    url: "/v2/admin/overview/",
    method: "GET",
    isAuthenticated: true,
  });
};

/**
 * Admin: Get a single user's full detail (super users only).
 * GET /v2/admin/users/:user_id/
 */
const getAdminUser = async ({ userId }) => {
  return await v2Client({
    url: `/v2/admin/users/${userId}/`,
    method: "GET",
    isAuthenticated: true,
  });
};

/**
 * Admin: Update a user (super users only). Send any subset of
 * { fname, lname, email, is_superadmin, is_verified }. Returns full detail.
 * PATCH /v2/admin/users/:user_id/
 */
const updateAdminUser = async ({ userId, body }) => {
  return await v2Client({
    url: `/v2/admin/users/${userId}/`,
    method: "PATCH",
    body,
    isAuthenticated: true,
  });
};

/**
 * Admin: Hard-delete a user (super users only). 204 on success; 400 on
 * self-delete; 409 if referenced by protected records.
 * DELETE /v2/admin/users/:user_id/
 */
const deleteAdminUser = async ({ userId }) => {
  return await v2Client({
    url: `/v2/admin/users/${userId}/`,
    method: "DELETE",
    isAuthenticated: true,
  });
};

/**
 * Admin: A user's activity timeline (paginated, newest first).
 * GET /v2/admin/users/:user_id/activity/
 */
const getAdminUserActivity = async ({ userId, page = 1, pageSize = 20 }) => {
  return await v2Client({
    url: `/v2/admin/users/${userId}/activity/`,
    method: "GET",
    isAuthenticated: true,
    queryParams: { page, page_size: pageSize },
  });
};

/**
 * Admin: Trigger a password-reset email for a user (super users only).
 * POST /v2/admin/users/:user_id/send-password-reset/
 */
const sendAdminUserPasswordReset = async ({ userId }) => {
  return await v2Client({
    url: `/v2/admin/users/${userId}/send-password-reset/`,
    method: "POST",
    isAuthenticated: true,
  });
};

/**
 * Admin: Get wearable assignment history (super users only)
 * GET /v2/admin/wearables/:mac_address/assignments/
 */
const getWearableAssignments = async ({
  macAddress,
  page = 1,
  pageSize = 20,
}) => {
  const hyphenMac = macAddress.replace(/:/g, "-");
  return await v2Client({
    url: `/v2/admin/wearables/${hyphenMac}/assignments/`,
    method: "GET",
    isAuthenticated: true,
    queryParams: { page, page_size: pageSize },
  });
};

/**
 * Admin: List every wearable assignment in a session (any workspace), joined
 * to manufacturing records where the MAC matches a known unit.
 * GET /v2/admin/sessions/:session_id/wearables/
 */
const getSessionWearables = async ({ sessionId, page = 1, pageSize = 100 }) => {
  return await v2Client({
    url: `/v2/admin/sessions/${sessionId}/wearables/`,
    method: "GET",
    isAuthenticated: true,
    queryParams: { page, page_size: pageSize },
  });
};

// ============================================
// Admin: Manufacturing (Batches / Wearables / Events)
// Superuser-only. Not workspace-scoped.
// ============================================

/**
 * Normalize a BLE MAC to the bare uppercase form the API expects in URLs.
 * Accepts separators (a1:b2:.. / a1-b2-..) and returns A1B2C3D4E5F6.
 */
const normalizeBleMac = (mac = "") =>
  mac.replace(/[^0-9a-fA-F]/g, "").toUpperCase();

/**
 * Admin: List manufacturing batches (paginated). search matches code or cm_name.
 * GET /v2/admin/batches/
 */
/**
 * The newest case software release on a channel ("production" or "dev"), with
 * presigned download links: { version, tag, base_version, product_version,
 * channel, released_at, notes, artifacts: { bundle: { url, size, sha256 } } }.
 * Superadmins only (403 otherwise).
 */
const getCaseSoftwareLatest = async ({ channel = "production" } = {}) => {
  return await v2Client({
    url: "/v2/case-software/latest/",
    method: "GET",
    isAuthenticated: true,
    queryParams: { channel },
  });
};

/**
 * The newest wearable firmware (OTA zip for Bluetooth DFU) on a channel
 * ("production" or "dev"), with a presigned download link: { version, tag,
 * base_version, product_version, channel, released_at, git_sha, notes,
 * artifacts: { ota: { url, size, sha256 } } }. Superadmins only (403
 * otherwise). Not cached: the link expires.
 */
const getWearableFirmwareLatest = async ({ channel = "production" } = {}) => {
  return await v2Client({
    url: "/v2/wearable-firmware/latest/",
    method: "GET",
    isAuthenticated: true,
    queryParams: { channel },
  });
};

/**
 * Every active wearable (not scrapped or retired) in one list, for mapping a
 * MAC to the number printed on its case: [{ ble_mac, macAddress, serial,
 * status, field_label, field_label_display }]. Any signed-in user.
 */
const getWearableLabels = async () => {
  return await v2Client({
    url: "/v2/wearables/",
    method: "GET",
    isAuthenticated: true,
    cacheTTL: CACHE_TTL,
  });
};

/**
 * A channel's wearable firmware releases, newest first, without download
 * links: { channel, releases: [{ version, tag, base_version, product_version,
 * channel, released_at, git_sha, size }] }. Superadmins only.
 */
const getWearableFirmwareReleases = async ({ channel = "production" } = {}) => {
  return await v2Client({
    url: "/v2/wearable-firmware/releases/",
    method: "GET",
    isAuthenticated: true,
    queryParams: { channel },
    cacheTTL: CACHE_TTL,
  });
};

/** One wearable firmware release by tag, shaped like the latest. */
const getWearableFirmwareVersion = async ({ tag, channel = "production" }) => {
  return await v2Client({
    url: `/v2/wearable-firmware/${encodeURIComponent(tag)}/`,
    method: "GET",
    isAuthenticated: true,
    queryParams: { channel },
  });
};

const getBatches = async ({ page = 1, pageSize = 20, search = "" }) => {
  return await v2Client({
    url: "/v2/admin/batches/",
    method: "GET",
    isAuthenticated: true,
    queryParams: {
      page,
      page_size: pageSize,
      ...(search && { search }),
    },
  });
};

/**
 * Admin: Create a batch.
 * POST /v2/admin/batches/
 */
const createBatch = async ({ body }) => {
  return await v2Client({
    url: "/v2/admin/batches/",
    method: "POST",
    body,
    isAuthenticated: true,
  });
};

/**
 * Admin: Get a batch detail (adds status_counts).
 * GET /v2/admin/batches/{id}/
 */
const getBatch = async ({ batchId }) => {
  return await v2Client({
    url: `/v2/admin/batches/${batchId}/`,
    method: "GET",
    isAuthenticated: true,
  });
};

/**
 * Admin: Partially update a batch.
 * PATCH /v2/admin/batches/{id}/
 */
const updateBatch = async ({ batchId, body }) => {
  return await v2Client({
    url: `/v2/admin/batches/${batchId}/`,
    method: "PATCH",
    body,
    isAuthenticated: true,
  });
};

/**
 * Admin: List wearables (paginated).
 * Filters: status, batchId, unitType. search matches serial or MAC.
 * GET /v2/admin/wearables/
 */
const getWearables = async ({
  page = 1,
  pageSize = 20,
  search = "",
  status = "",
  batchId = "",
  unitType = "",
  validationStage = "",
  ordering = "",
}) => {
  return await v2Client({
    url: "/v2/admin/wearables/",
    method: "GET",
    isAuthenticated: true,
    queryParams: {
      page,
      page_size: pageSize,
      ...(search && { search }),
      ...(status && { status }),
      ...(batchId && { batch_id: batchId }),
      ...(unitType && { unit_type: unitType }),
      ...(validationStage && { validation_stage: validationStage }),
      // Server-side sort: the list is paginated, so ordering client-side
      // would only reorder the pages already fetched. Empty = the API's
      // default (newest first). See WEARABLE_ORDERINGS in the backend.
      ...(ordering && { ordering }),
    },
  });
};

/**
 * Admin: Create / allocate a wearable.
 * POST /v2/admin/wearables/
 */
const createWearable = async ({ body }) => {
  return await v2Client({
    url: "/v2/admin/wearables/",
    method: "POST",
    body,
    isAuthenticated: true,
  });
};

/**
 * Admin: Get a wearable by BLE MAC (case-insensitive lookup).
 * GET /v2/admin/wearables/{ble_mac}/
 */
const getWearable = async ({ bleMac }) => {
  return await v2Client({
    url: `/v2/admin/wearables/${normalizeBleMac(bleMac)}/`,
    method: "GET",
    isAuthenticated: true,
  });
};

/**
 * Admin: Partially update a wearable (incl. status lifecycle transitions).
 * PATCH /v2/admin/wearables/{ble_mac}/
 */
const updateWearable = async ({ bleMac, body }) => {
  return await v2Client({
    url: `/v2/admin/wearables/${normalizeBleMac(bleMac)}/`,
    method: "PATCH",
    body,
    isAuthenticated: true,
  });
};

/**
 * Admin: Log a validation step attempt. Returns the FULL updated wearable
 * detail (the tree advances, and status may flip to `validated`), so callers
 * re-render from the response.
 * POST /v2/admin/wearables/{ble_mac}/attempts/
 * body: { sub_stage, step, result: "pass"|"fail", data?: {}, notes?: "" }
 */
const createWearableAttempt = async ({ bleMac, body }) => {
  return await v2Client({
    url: `/v2/admin/wearables/${normalizeBleMac(bleMac)}/attempts/`,
    method: "POST",
    body,
    isAuthenticated: true,
  });
};

/**
 * Admin: Full step-attempt history for a wearable, newest first (audit view).
 * GET /v2/admin/wearables/{ble_mac}/attempts/
 */
const getWearableAttempts = async ({ bleMac, page = 1, pageSize = 50 }) => {
  return await v2Client({
    url: `/v2/admin/wearables/${normalizeBleMac(bleMac)}/attempts/`,
    method: "GET",
    isAuthenticated: true,
    queryParams: { page, page_size: pageSize },
  });
};

/**
 * Get workspace members (memberships)
 * GET /v2/workspaces/{workspace_id}/memberships/
 */
const getMembers = async ({ workspaceId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/memberships/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Update a member's role
 * PATCH /v2/workspaces/{workspace_id}/memberships/{membership_id}/
 */
const updateMember = async ({ workspaceId, memberId, body }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/memberships/${memberId}/`,
    method: "PATCH",
    body,
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Remove a member from the workspace
 * DELETE /v2/workspaces/{workspace_id}/memberships/{membership_id}/
 */
const removeMember = async ({ workspaceId, memberId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/memberships/${memberId}/`,
    method: "DELETE",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Sign a member out on every device (workspace admins)
 * POST /v2/workspaces/{workspace_id}/memberships/{membership_id}/sign-out/
 */
const signOutMemberEverywhere = async ({ workspaceId, memberId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/memberships/${memberId}/sign-out/`,
    method: "POST",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Sign yourself out on every device, this one included
 * POST /v2/me/sign-out-everywhere/
 */
const signOutEverywhere = async () => {
  return await v2Client({
    url: "/v2/me/sign-out-everywhere/",
    method: "POST",
    isAuthenticated: true,
    expireSessionOn401: false,
  });
};

/**
 * Sign any user out on every device (superadmin)
 * POST /v2/admin/users/{user_id}/sign-out-everywhere/
 */
const adminSignOutUserEverywhere = async ({ userId }) => {
  return await v2Client({
    url: `/v2/admin/users/${userId}/sign-out-everywhere/`,
    method: "POST",
    isAuthenticated: true,
  });
};

/**
 * Send an invite to a workspace (V2 invites endpoint)
 * POST /v2/workspaces/{workspace_id}/invites/
 */
const sendInvite = async ({ workspaceId, email, role }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/invites/`,
    method: "POST",
    body: { email, role },
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Get pending invites for a workspace
 * GET /v2/workspaces/{workspace_id}/invites/
 */
const getInvites = async ({ workspaceId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/invites/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Revoke a pending invite
 * DELETE /v2/workspaces/{workspace_id}/invites/{invite_id}/
 */
const revokeInvite = async ({ workspaceId, inviteId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/invites/${inviteId}/`,
    method: "DELETE",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * The team's shareable invite link ({ invite_link: {...} | null })
 * GET /v2/workspaces/{workspace_id}/teams/{team_id}/invite-link/
 */
const getTeamInviteLink = async ({ workspaceId, teamId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/invite-link/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Make a new team invite link; the previous one stops working
 * POST /v2/workspaces/{workspace_id}/teams/{team_id}/invite-link/
 */
const createTeamInviteLink = async ({ workspaceId, teamId, body = {} }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/invite-link/`,
    method: "POST",
    body,
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Revoke the team's invite link
 * DELETE /v2/workspaces/{workspace_id}/teams/{team_id}/invite-link/
 */
const revokeTeamInviteLink = async ({ workspaceId, teamId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/invite-link/`,
    method: "DELETE",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Public: what a team invite link points to and which sign-up to show.
 * Throws with status 404 (invalid) or 410 (expired / revoked).
 * GET /v2/team-invites/{token}/
 */
const resolveTeamInvite = async ({ token }) => {
  return await v2Client({
    url: `/v2/team-invites/${encodeURIComponent(token)}/`,
    method: "GET",
    isAuthenticated: false,
  });
};

/**
 * Public: a parent or guardian gives consent for their child from a team
 * invite link. Creates a pending claim; the API emails their private link.
 * POST /v2/team-invites/{token}/claims/
 */
const createGuardianClaim = async ({ token, body }) => {
  return await v2Client({
    url: `/v2/team-invites/${encodeURIComponent(token)}/claims/`,
    method: "POST",
    body,
    isAuthenticated: false,
  });
};

/**
 * Public: a guardian's consent choices, by the private link in their email
 * GET /v2/guardian-consent/{token}/
 */
const getGuardianConsent = async ({ token }) => {
  return await v2Client({
    url: `/v2/guardian-consent/${encodeURIComponent(token)}/`,
    method: "GET",
    isAuthenticated: false,
  });
};

/**
 * Public: change a guardian's consent choices (each change is a new record)
 * POST /v2/guardian-consent/{token}/
 */
const updateGuardianConsent = async ({ token, body }) => {
  return await v2Client({
    url: `/v2/guardian-consent/${encodeURIComponent(token)}/`,
    method: "POST",
    body,
    isAuthenticated: false,
  });
};

/**
 * Parent sign-ups on a team ({ claims: [...] }); status pending (default),
 * approved or all
 * GET /v2/workspaces/{workspace_id}/teams/{team_id}/guardian-claims/
 */
const getGuardianClaims = async ({ workspaceId, teamId, status }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/guardian-claims/`,
    method: "GET",
    queryParams: status ? { status } : undefined,
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Match a parent sign-up to a roster player ({ player_id }) or add the child
 * to the roster ({ new_player: { name } })
 * POST /v2/workspaces/{workspace_id}/teams/{team_id}/guardian-claims/{claim_id}/approve/
 */
const approveGuardianClaim = async ({ workspaceId, teamId, claimId, body }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/guardian-claims/${claimId}/approve/`,
    method: "POST",
    body,
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Reject a parent sign-up: the parent is told and their details are deleted
 * POST /v2/workspaces/{workspace_id}/teams/{team_id}/guardian-claims/{claim_id}/reject/
 */
const rejectGuardianClaim = async ({ workspaceId, teamId, claimId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/guardian-claims/${claimId}/reject/`,
    method: "POST",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Under-16 team: every player with their parent and consent choices
 * GET /v2/workspaces/{workspace_id}/teams/{team_id}/consent/
 */
const getTeamConsent = async ({ workspaceId, teamId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/consent/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * One player's consent and its history
 * GET /v2/workspaces/{workspace_id}/teams/{team_id}/players/{player_id}/consent/
 */
const getPlayerConsent = async ({ workspaceId, teamId, playerId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/players/${playerId}/consent/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Email a player's parent a new private link to change their consent (the
 * earlier link stops working). At most once every 10 minutes.
 * POST /v2/workspaces/{workspace_id}/teams/{team_id}/players/{player_id}/resend-consent-link/
 */
const resendConsentLink = async ({ workspaceId, teamId, playerId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/players/${playerId}/resend-consent-link/`,
    method: "POST",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Stop a player's parent's private consent link working. A resend issues a
 * new one.
 * POST /v2/workspaces/{workspace_id}/teams/{team_id}/players/{player_id}/revoke-consent-link/
 */
const revokeConsentLink = async ({ workspaceId, teamId, playerId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/players/${playerId}/revoke-consent-link/`,
    method: "POST",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Every consent record the club holds, for it to keep (workspace admins)
 * GET /v2/workspaces/{workspace_id}/consent-records/export/
 */
const exportConsentRecords = async ({ workspaceId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/consent-records/export/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Everything held about one player, as JSON (workspace admins)
 * GET /v2/workspaces/{workspace_id}/teams/{team_id}/players/{player_id}/export/
 */
const exportPlayerData = async ({ workspaceId, teamId, playerId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/players/${playerId}/export/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Delete one player's data now (workspace admins; confirm with their name)
 * POST /v2/workspaces/{workspace_id}/teams/{team_id}/players/{player_id}/delete-data/
 */
const deletePlayerData = async ({
  workspaceId,
  teamId,
  playerId,
  confirmName,
}) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/teams/${teamId}/players/${playerId}/delete-data/`,
    method: "POST",
    body: { confirm_name: confirmName },
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Accept an invite using a token
 * POST /v2/invites/accept/
 */
const acceptInvite = async ({ token }) => {
  return await v2Client({
    url: "/v2/invites/accept/",
    method: "POST",
    body: { token },
    isAuthenticated: true,
  });
};

/**
 * Get invites for the current user
 * GET /v2/me/invites/
 */
const getMyInvites = async () => {
  return await v2Client({
    url: "/v2/me/invites/",
    method: "GET",
    isAuthenticated: true,
  });
};

// ============================================
// API Key Endpoints
// ============================================

/**
 * Get API keys for a workspace
 * GET /v2/workspaces/{workspace_id}/api-keys/
 */
const getApiKeys = async ({ workspaceId, includeRevoked }) => {
  const queryParams = includeRevoked ? { include_revoked: true } : undefined;
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/api-keys/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
    queryParams,
  });
};

/**
 * Create a new API key
 * POST /v2/workspaces/{workspace_id}/api-keys/
 */
const createApiKey = async ({ workspaceId, body }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/api-keys/`,
    method: "POST",
    isAuthenticated: true,
    workspaceId,
    body,
  });
};

/**
 * Get a single API key detail
 * GET /v2/workspaces/{workspace_id}/api-keys/{key_id}/
 */
const getApiKey = async ({ workspaceId, keyId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/api-keys/${keyId}/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Revoke an API key (soft delete)
 * DELETE /v2/workspaces/{workspace_id}/api-keys/{key_id}/
 */
const revokeApiKey = async ({ workspaceId, keyId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/api-keys/${keyId}/`,
    method: "DELETE",
    isAuthenticated: true,
    workspaceId,
  });
};

// ============================================
// Logo Endpoints
// ============================================

/**
 * Get a presigned S3 GET URL for the workspace logo
 * GET /v2/workspaces/{workspace_id}/logo/
 * Returns 404 if no logo has been uploaded yet
 */
const getWorkspaceLogo = async ({ workspaceId }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/logo/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Get a presigned S3 POST for uploading a workspace logo (10 MB max)
 * POST /v2/workspaces/{workspace_id}/logo/upload-url/
 * content_type: one of image/jpeg, image/png, image/gif, image/webp, image/svg+xml
 * Returns { upload_url, fields, max_bytes, expires_in }. Call
 * confirmWorkspaceLogo after the upload.
 */
const getWorkspaceLogoUploadUrl = async ({ workspaceId, contentType }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/logo/upload-url/`,
    method: "POST",
    body: { content_type: contentType },
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Use the logo just uploaded to S3
 * POST /v2/workspaces/{workspace_id}/logo/confirm/
 */
const confirmWorkspaceLogo = async ({ workspaceId, contentType }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/logo/confirm/`,
    method: "POST",
    body: { content_type: contentType },
    isAuthenticated: true,
    workspaceId,
  });
};

/**
 * Find teams, athletes and sessions in a workspace by name (command palette)
 * GET /v2/workspaces/{workspace_id}/search/?q=
 * Returns { query, teams, athletes, sessions }, up to 5 of each.
 */
const searchWorkspace = async ({ workspaceId, query }) => {
  return await v2Client({
    url: `/v2/workspaces/${workspaceId}/search/`,
    method: "GET",
    isAuthenticated: true,
    workspaceId,
    queryParams: { q: query },
    cacheTTL: CACHE_TTL,
  });
};

const betaAPI = {
  // Auth
  register,
  login,
  logout,
  verifyEmail,
  resendVerificationEmail,
  requestPasswordReset,
  confirmPasswordReset,
  // User
  getCurrentUser,
  updateCurrentUser,
  confirmPassword,
  changePassword,
  deleteAccount,
  exportMyData,
  deleteMyData,
  selectiveDeleteMyData,
  // Workspaces
  getWorkspaces,
  searchWorkspace,
  createWorkspace,
  getWorkspace,
  updateWorkspace,
  deleteWorkspace,
  leaveWorkspace,
  inviteMember,
  getMembers,
  updateMember,
  removeMember,
  signOutMemberEverywhere,
  signOutEverywhere,
  adminSignOutUserEverywhere,
  sendInvite,
  getInvites,
  revokeInvite,
  acceptInvite,
  getTeamInviteLink,
  createTeamInviteLink,
  revokeTeamInviteLink,
  resolveTeamInvite,
  getGuardianClaims,
  getTeamConsent,
  getPlayerConsent,
  exportPlayerData,
  resendConsentLink,
  revokeConsentLink,
  exportConsentRecords,
  deletePlayerData,
  approveGuardianClaim,
  rejectGuardianClaim,
  createGuardianClaim,
  getGuardianConsent,
  updateGuardianConsent,
  getMyInvites,
  getDashboard,
  // Teams
  getTeam,
  createTeam,
  updateTeam,
  deleteTeam,
  getTeamOverview,
  getTeamLoadChart,
  getTeamHeaderStats,
  getTeamAttendance,
  // Players
  createPlayer,
  updatePlayer,
  deletePlayer,
  changePlayerTeam,
  getPlayerLoadChart,
  // Sessions
  getTeamSessions,
  getSession,
  getSessionLoadGraph,
  uploadImpactData,
  // Activity
  getWorkspaceActivity,
  getTeamActivity,
  // Admin
  getAdminOverview,
  getAdminWorkspaces,
  getWorkspaceConsentSettings,
  updateWorkspaceConsentSettings,
  getAdminUsers,
  getAdminTeams,
  getAdminPlayers,
  getAdminUser,
  updateAdminUser,
  deleteAdminUser,
  getAdminUserActivity,
  sendAdminUserPasswordReset,
  getWearableAssignments,
  getSessionWearables,
  // Admin: Case software
  getCaseSoftwareLatest,
  getWearableFirmwareLatest,
  getWearableLabels,
  getWearableFirmwareReleases,
  getWearableFirmwareVersion,
  // Admin: Manufacturing
  getBatches,
  createBatch,
  getBatch,
  updateBatch,
  getWearables,
  createWearable,
  getWearable,
  updateWearable,
  createWearableAttempt,
  getWearableAttempts,
  normalizeBleMac,
  // API Keys
  getApiKeys,
  createApiKey,
  getApiKey,
  revokeApiKey,
  // Logo
  getWorkspaceLogo,
  getWorkspaceLogoUploadUrl,
  confirmWorkspaceLogo,
};

export default betaAPI;
